/**
 * 要件定義の成果物 API クライアント。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 要件定義の成果物（ナラティブ・業務フロー・レビュー状態）を読み書きする API クライアント
 * @graph-connects /spec-requirements [calls] 成果物 API
 */

import { request } from './_request.js';

/** 一覧に出す案件 1 件。 @graph-connects none */
export interface SpecProjectSummary {
  pjKey: string;
  pjTitle: string;
  flowCount: number;
  hasNarrative: boolean;
  status: string;
  /** AI レビュー待ちの成果物の数。`status` は案件に 1 つしか出せないので別に持つ */
  aiReviewWaiting: number;
  /** DLE レビュー待ちの成果物の数 */
  dleReviewWaiting: number;
  /** ナラティブか業務フローを自分が最初に保存したか（レビュー依頼の前でも立つ） */
  createdByMe: boolean;
  /** DLE レビュー待ちがあり、かつ自分がその案件の DLE（主） */
  dleWaitingForMe: boolean;
  /** 同じく、自分が DLE サブ */
  dleWaitingForMeAsSub: boolean;
  /** 全成果物が DLE 承認済みで PL の判断待ち（差し戻し後の再確認待ちを含む） */
  plReviewWaiting: boolean;
  /** PL の承認待ちで、かつ自分がその案件の PL */
  plWaitingForMe: boolean;
  /** 案件の事業（母票から引けなければ空） */
  business: string;
  /** 案件の責任チーム（母票から引けなければ空） */
  team: string;
  /** DLE 待ちなのに担当が引けない（引けないと「自分宛て」が false になり待ちが無いのと同じ見え方になる） */
  dleAssigneeMissing: boolean;
  updatedAt: string;
}

/**
 * 案件に 1 つしかない成果物のレビュー対象 ID。**API のパス（`/reviews/{targetId}`）にそのまま乗る値**で、
 * domain の `SINGLETON_REVIEW_TARGETS` と同じもの。
 *
 * domain の `@cortex/domain-spec-flow` は `node:fs` と Firestore SDK を読むので画面からは import
 * できない。写しはここ 1 か所だけに置き、**節ごとに文字列を書かない**（節に書くと、対象を 1 つ
 * 間違えても型では落ちず、別の成果物のレビューを黙って動かす）。
 * @graph-connects none
 */
export const SINGLETON_REVIEW_TARGETS = {
  narrative: 'narrative',
  features: 'features',
  integrationTest: 'integration_test',
  validationList: 'validation_list',
  decisionLog: 'decision_log',
} as const;

/** 指摘 1 件（未解決・解決済みで共通）。 @graph-connects none */
export interface SpecFinding {
  id: string;
  perspective: string;
  severity: string;
  summary: string;
  /** 指摘が指す箇所（業務フローなら F5 など）。無ければ空文字 */
  target: string;
  /**
   * 出した役（`ai` / `dle`）。**API が古い間・この欄を入れる前の指摘には来ない。**
   *
   * 来ないときは出自を出さない。「不明」と出すと、まだ記録していない指摘と
   * 出自を取り違えた指摘が同じ見え方になる。
   */
  foundByRole?: string;
  /** 出した人（AI レビューは `ai:` が前に付く）。API が古い間は来ない */
  foundBy?: string;
}

/** 片付いた指摘 1 件。 @graph-connects none */
export interface SpecResolvedFinding extends SpecFinding {
  /** `fixed` / `dismissed` / `withdrawn` */
  resolution: string;
  /** 直した内容・見送る理由・何が誤っていたか */
  resolutionNote: string;
}

/** 成果物 1 つのレビュー状態。5 つの成果物と業務フローで共有する。 @graph-connects none */
export interface SpecReviewState {
  status: string;
  targetVersion: number;
  carriedVersions: number[];
  openFindings: SpecFinding[];
  /**
   * 片付いた指摘（直した・見送った・取り消した）。**API が古い間は来ない。**
   *
   * 来ないときは経緯の節を出さない（空配列として扱うと「片付いた指摘は無い」に見える）。
   */
  resolvedFindings?: SpecResolvedFinding[];
}

/**
 * レビューの出来事 1 件（依頼・提出・指摘の解決・DLE 承認の取り消し・取り下げ）。
 *
 * 誰がいつ何をしたかは**ここにしか無い**。レビュー本体は操作のたびに上書きされるので、
 * 「誰が直したと記録したか」「何回差し戻されたか」は出来事の記録から読む。
 * @graph-connects none
 */
export interface SpecReviewEvent {
  seq: number;
  kind: string;
  at: string;
  actor: string;
  /** 提出・DLE 承認の取り消しだけが持つ（`ai` / `dle`） */
  role: string | null;
  targetVersion: number;
  status: string;
  previousStatus: string | null;
  findings: SpecResolvedFinding[];
  /** この回に足した（提出）・扱いを変えた（解決）指摘の ID */
  changedFindingIds: string[];
  /** 中身が変わった版で依頼し直したときに捨てた指摘 */
  discardedFindings: SpecResolvedFinding[];
}

/** 業務フロー 1 件。 @graph-connects none */
export interface SpecFlow {
  flowId: string;
  title: string;
  mermaid: string;
  before: string;
  after: string;
  /** 図を書いた言語。**`ja` とは限らない。** 編集は常にこの言語の図に対して行う */
  sourceLocale?: SpecReadLocale;
  /** 求めた言語の訳が無い・古いため原文の図が返ったか。API が古い間は来ない */
  untranslated?: boolean;
  /** 略号（F = ステップ ID / P = Phase）の参照先。API が古い間は来ない */
  steps?: { id: string; label: string; phase: number }[];
  /** 取り下げ済みならその記録。有効な図は null。API が古い間は来ない */
  retired?: { at: string; by: string; reason: string } | null;
  version: number;
  updatedAt: string;
  updatedBy: string;
  review: SpecReviewState | null;
}

/** 取り下げ・解除の結果。 @graph-connects none */
export interface SpecFlowRetireResult {
  pjKey: string;
  flowId: string;
  version: number;
  changed: boolean;
}

/** ナラティブ。 @graph-connects none */
export interface SpecNarrative {
  review: SpecReviewState | null;
  why: string;
  change: string;
  scope: string;
  verify: string;
  open: string[];
  /**
   * 本文を書いた言語。**`ja` とは限らない。** 編集は常にこの言語の本文に対して行う。
   * API が古い間は来ないので任意。
   */
  sourceLocale?: SpecReadLocale;
  /**
   * 求めた言語の訳が無い・古いため、原文が返ってきたか。
   *
   * 画面はこれを見て未翻訳の印を出す。API が古い間は来ないので任意。
   */
  untranslated?: boolean;
  version: number;
  updatedAt: string;
  updatedBy: string;
}

/**
 * 成果物を読む言語。
 *
 * **`vi` を `en` に畳まない。** バグ評価などの `useApiLocale` は ja/en の 2 値に畳んでいるが、
 * 要件定義の成果物はベトナム語の訳を実際に保存しているので、畳むとベトナム語話者が英語を読む。
 * @graph-connects none
 */
export type SpecReadLocale = 'ja' | 'en' | 'vi';

/** プロジェクト憲章。欄は起票フォームと同じ 10 個で、空欄は空文字。 @graph-connects none */
export interface SpecCharter {
  /**
   * どこから読んだか。`firestore` は起票の仕組みが保存した憲章、`specSheet` は案件仕様書の
   * 「プロジェクト憲章」タブ（起票の仕組みより前の案件）。API が古い間は来ない。
   */
  source?: 'firestore' | 'specSheet';
  /** 本文を書いた言語。**`ja` とは限らない。** API が古い間は来ない */
  sourceLocale?: SpecReadLocale;
  /** 求めた言語の訳が無い・古いため原文が返ってきたか。API が古い間は来ない */
  untranslated?: boolean;
  purpose: string;
  background: string;
  goal: string;
  deliverables: string;
  preconditions: string;
  constraints: string;
  schedule: string;
  cost: string;
  structure: string;
  notes: string;
  /** 案件仕様書から読んだ憲章は版を持たない */
  version: number | null;
  /** 案件仕様書から読んだ憲章は更新日時を持たない */
  updatedAt: string | null;
  /** 案件仕様書から読んだ憲章は書き手を持たない */
  updatedBy: string | null;
}

/** 機能一覧の 1 行。 @graph-connects none */
export interface SpecFeatureRow {
  /** 固定番号。読んだ値をそのまま返して保存する。新しく足した行には無い */
  no?: number;
  area: string;
  requirement: string;
  asis: string;
  tobe: string;
  note: string;
}

/** 機能一覧。 @graph-connects none */
export interface SpecFeatures {
  review: SpecReviewState | null;
  rows: SpecFeatureRow[];
  /** 行を書いた言語。**`ja` とは限らない。** API が古い間は来ない */
  sourceLocale?: SpecReadLocale;
  /** 求めた言語の訳が無い・古いため原文の行が返ってきたか。API が古い間は来ない */
  untranslated?: boolean;
  version: number;
  updatedAt: string;
  updatedBy: string;
}

/** 総合テストの 1 行。 @graph-connects none */
export interface SpecIntegrationTestRow {
  /** 固定番号。読んだ値をそのまま返して保存する。新しく足した行には無い */
  no?: number;
  scene: string;
  scenario: string;
  screen: string;
  given: string;
  operation: string;
  expected: string;
  covers: string;
}

/** 総合テスト。実施の記録は持たない。 @graph-connects none */
export interface SpecIntegrationTest {
  /** レビュー状態。まだ依頼していなければ null。API が古い間は来ない */
  review?: SpecReviewState | null;
  /** 行を書いた言語。**`ja` とは限らない。** API が古い間は来ない */
  sourceLocale?: SpecReadLocale;
  /** 求めた言語の訳が無い・古いため原文の行が返ってきたか。API が古い間は来ない */
  untranslated?: boolean;
  rows: SpecIntegrationTestRow[];
  version: number;
  updatedAt: string;
  updatedBy: string;
}

/** バリデーション一覧の 1 行。 @graph-connects none */
export interface SpecValidationRow {
  /** 固定番号。読んだ値をそのまま返して保存する。新しく足した行には無い */
  no?: number;
  screen: string;
  position: string;
  condition: string;
  message: string;
}

/** バリデーション一覧。応答ステータスやログの文言は持たない。 @graph-connects none */
export interface SpecValidationList {
  /** レビュー状態。まだ依頼していなければ null。API が古い間は来ない */
  review?: SpecReviewState | null;
  /** 行を書いた言語。**`ja` とは限らない。** API が古い間は来ない */
  sourceLocale?: SpecReadLocale;
  /** 求めた言語の訳が無い・古いため原文の行が返ってきたか。API が古い間は来ない */
  untranslated?: boolean;
  rows: SpecValidationRow[];
  version: number;
  updatedAt: string;
  updatedBy: string;
}

/** 決定記録の 1 行。 @graph-connects none */
export interface SpecDecisionRow {
  /** 固定番号。読んだ値をそのまま返して保存する。新しく足した行には無い */
  no?: number;
  topic: string;
  decision: string;
  rejected: string;
  why: string;
}

/** 決定記録。議事録の全文は持たない。 @graph-connects none */
export interface SpecDecisionLog {
  /** レビュー状態。まだ依頼していなければ null。API が古い間は来ない */
  review?: SpecReviewState | null;
  /** 行を書いた言語。**`ja` とは限らない。** API が古い間は来ない */
  sourceLocale?: SpecReadLocale;
  /** 求めた言語の訳が無い・古いため原文の行が返ってきたか。API が古い間は来ない */
  untranslated?: boolean;
  rows: SpecDecisionRow[];
  version: number;
  updatedAt: string;
  updatedBy: string;
}

/**
 * 案件ごとの補足欄（markdown の自由欄）。
 *
 * 決まった表に入らない情報の置き場。**レビュー対象ではないので `review` を持たない**
 * （自由欄は形式を機械で縛れず、AI が観点を持てない）。
 * @graph-connects none
 */
export interface SpecProjectNotes {
  /** 本文（markdown）。見出しの付け方は書く人に任せる */
  body: string;
  /** 本文を書いた言語。**`ja` とは限らない。** API が古い間は来ない */
  sourceLocale?: SpecReadLocale;
  /** 求めた言語の訳が無い・古いため原文が返ってきたか。API が古い間は来ない */
  untranslated?: boolean;
  version: number;
  updatedAt: string;
  updatedBy: string;
}

/** デザイン成果物 1 件（名前と URL の 2 つだけ）。 @graph-connects none */
export interface SpecDesignLink {
  /** 人が探すときの名前（例: カートのラフ）。画面ではこれがリンクテキストになる */
  name: string;
  /** 置き場の URL（Figma など）。API が http:// / https:// だけを通す */
  url: string;
}

/**
 * 案件ごとのデザイン成果物の在り処。
 *
 * ラフも確定デザインもデザイナーが Figma などで作るので、画面が持つのは「どこにあるか」だけ。
 * ラフ / 確定を区別する列は無い（名前に書く）。**レビュー対象ではないので `review` を持たない**。
 * @graph-connects none
 */
export interface SpecDesignLinks {
  links: SpecDesignLink[];
  /** 本文を書いた言語。**`ja` とは限らない。** API が古い間は来ない */
  sourceLocale?: SpecReadLocale;
  /** 求めた言語の訳が無い・古いため原文が返ってきたか。API が古い間は来ない */
  untranslated?: boolean;
  version: number;
  updatedAt: string;
  updatedBy: string;
}

/** 案件の成果物。 @graph-connects none */
export interface SpecProjectDetail {
  pjKey: string;
  pjTitle: string;
  charter: SpecCharter | null;
  /**
   * 憲章が null のとき、無いのではなく読めなかった理由。`specSheetUnreadable` は案件仕様書を
   * 読めなかった、`error` は読み取りの失敗。読めていれば null。API が古い間は来ない
   */
  charterReadFailure?: 'specSheetUnreadable' | 'error' | null;
  narrative: SpecNarrative | null;
  flows: SpecFlow[];
  features: SpecFeatures | null;
  integrationTest: SpecIntegrationTest | null;
  validationList: SpecValidationList | null;
  decisionLog: SpecDecisionLog | null;
  /** 補足欄。まだ書かれていなければ null。API が古い間は来ない */
  projectNotes?: SpecProjectNotes | null;
  /** デザイン成果物。まだ登録されていなければ null。API が古い間は来ない */
  designLinks?: SpecDesignLinks | null;
}

/**
 * ナラティブの保存で送る本文。
 *
 * 章ごとの行数上限は API が持つ（超えたら 400 とメッセージが返る）。画面側で数を持つと
 * 上限が変わったときに黙ってずれるので、**行数の判定は画面に写さない**。
 * @graph-connects none
 */
export interface SpecNarrativeInput {
  why: string;
  change: string;
  scope: string;
  verify: string;
  open: string[];
  /**
   * 本文を書いた言語。**新規作成のときだけ効く。**
   *
   * 既にある成果物では API が保存済みの値を守るので、ここで送った値は無視される。
   */
  sourceLocale?: SpecReadLocale;
}

/** 保存の結果。 @graph-connects none */
export interface SpecUpsertResult {
  pjKey: string;
  version: number;
  created: boolean;
  warnings: string[];
}

/**
 * レビューの提出。
 *
 * `severity` と `perspective` の取りうる値は API が持つ（不正なら 400）。画面に enum を
 * 複製すると API 側で増えたときに黙ってずれるので、画面は人が選ぶものだけを出す。
 * @graph-connects none
 */
export interface SpecReviewSubmitInput {
  reviewer: 'ai' | 'dle';
  findings: { perspective: string; severity: string; summary: string; target?: string }[];
}

/**
 * 次の 1 手。
 *
 * `runbook` は MCP のツール呼び出しとスキル名の列。画面では「Claude に頼むときの手順」
 * として出す（人が画面でそのまま実行するものではない）。
 * @graph-connects none
 */
export interface SpecNextStep {
  id: string;
  /** 今いる工程の名前（業務フロー・DLE レビューなど） */
  phase: string;
  action: string;
  reason: string;
  runbook: string[];
  waitingFor: string;
  pending: string[];
}

/** 業務フローの保存で送る本文。 @graph-connects none */
export interface SpecFlowInput {
  title: string;
  mermaid: string;
  before: string;
  after: string;
}

/** 機械チェックの結果。NG でも HTTP は 200 で、理由は errors に入る。 @graph-connects none */
export interface SpecFlowCheckResult {
  ok: boolean;
  errors: string[];
  warnings: string[];
  lanes: string[];
  steps: { id: string; label: string; kind: string; lane: string; phase: number; change: string }[];
}

/**
 * 憲章の保存で送る欄。全て任意（渡した欄だけ上書き）。
 *
 * **`untranslated` は外す。** 読む側だけの印（原文に落ちたかどうか）で、保存の入力ではない。
 * 外さないと編集用の下書き（10 欄の文字列）をこの型として扱えなくなる。
 * @graph-connects none
 */
export type SpecCharterInput = Omit<
  SpecCharter,
  'version' | 'updatedAt' | 'updatedBy' | 'untranslated'
>;

/** 指摘の解決 1 件。 @graph-connects none */
export interface SpecFindingResolutionInput {
  findingId: string;
  resolution: 'fixed' | 'dismissed' | 'withdrawn' | 'reopen';
  note?: string;
}

/**
 * 案件全体の PL レビュー。まだ判断が無ければ `status` が null で `entries` は空。
 *
 * 判断の選択肢・コメントの必須や上限は API が持つ。画面は値を複製せず、弾かれたら返った
 * メッセージを見せる。
 * @graph-connects none
 */
export interface SpecPlReview {
  pjKey: string;
  status: string | null;
  entries: { decision: string; comment: string; actor: string; at: string }[];
  /** 成果物ごとの状態。全成果物が並び、付けていないものは `unreviewed` */
  artifacts: SpecPlArtifactState[];
}

/** 成果物 1 つの PL の状態。値の選択肢は API が持つので文字列のまま持つ。 @graph-connects none */
export interface SpecPlArtifactState {
  artifact: string;
  status: string;
  comment: string;
  actor: string | null;
  at: string | null;
}

/** 成果物 1 つの PL の状態の提出。 @graph-connects none */
export interface SpecPlArtifactSubmitInput {
  status: string;
  comment?: string;
}

/** PL の判断の提出。 @graph-connects none */
export interface SpecPlReviewSubmitInput {
  decision: 'approved' | 'changes_requested';
  comment?: string;
}

/** レビュー操作のパスを組み立てる。 @graph-connects none */
function reviewPath(pjKey: string, targetId: string, action: string): string {
  const pj = encodeURIComponent(pjKey);
  return `/spec-requirements/${pj}/reviews/${encodeURIComponent(targetId)}/${action}`;
}

/** @graph-connects /spec-requirements [calls] 成果物 API */
export const specRequirementsApi = {
  /** @graph-connects /spec-requirements [calls] 案件一覧 */
  list: (): Promise<{ projects: SpecProjectSummary[] }> => request('/spec-requirements'),
  /**
   * レビュー待ちの成果物を案件横断で引く（古い順）。
   *
   * 状態は 1 つだけ渡す。AI レビュー待ちと DLE レビュー待ちは読む人が違うので、
   * 1 つの応答に混ぜない（画面でも節を分けて出す）。
   * @graph-connects /spec-requirements [calls] レビュー待ちの横断一覧
   */
  /**
   * 案件の成果物を、画面の言語で読む。
   *
   * `locale` は訳を持つ成果物にだけ効く。訳が無い・古いときは原文が返り、その成果物の
   * `untranslated` が立つ（空で隠さない）。
   * @graph-connects /spec-requirements [calls] 案件の成果物
   */
  detail: (pjKey: string, locale: SpecReadLocale = 'ja'): Promise<SpecProjectDetail> =>
    request(
      `/spec-requirements/${encodeURIComponent(pjKey)}` +
        // 原文はクエリを付けない。API は locale 省略を原文として扱うので同じ意味になり、
        // 既定の読み方で URL にパラメータが残らない
        (locale === 'ja' ? '' : `?locale=${locale}`),
    ),
  /**
   * レビューの出来事を古い順に引く。
   *
   * **必要になったときだけ引く。** 案件の詳細は成果物 6 つぶんのレビューを持つので、
   * 開いた時点で全ての経緯を引くと読まれない取得が 6 回走る。
   * @graph-connects /spec-requirements [calls] レビューの出来事
   */
  reviewEvents: (
    pjKey: string,
    targetId: string,
  ): Promise<{ pjKey: string; artifact: string; targetId: string; events: SpecReviewEvent[] }> =>
    request(reviewPath(pjKey, targetId, 'events')),
  /** @graph-connects /spec-requirements [calls] ナラティブの保存 */
  updateNarrative: (pjKey: string, data: SpecNarrativeInput): Promise<SpecUpsertResult> =>
    request(`/spec-requirements/${encodeURIComponent(pjKey)}/narrative`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  /**
   * 憲章を部分更新する。
   *
   * **渡した欄だけを上書きし、渡さなかった欄は今の値を残す。** 空文字を渡せばその欄を消せる
   * （未指定と区別する）ので、画面は触った欄だけを送る。
   * @graph-connects /spec-requirements [calls] 憲章の保存
   */
  updateCharter: (pjKey: string, data: Partial<SpecCharterInput>): Promise<SpecUpsertResult> =>
    request(`/spec-requirements/${encodeURIComponent(pjKey)}/charter`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  /** @graph-connects /spec-requirements [calls] 機能一覧の保存 */
  updateFeatures: (pjKey: string, rows: SpecFeatureRow[]): Promise<SpecUpsertResult> =>
    request(`/spec-requirements/${encodeURIComponent(pjKey)}/features`, {
      method: 'PUT',
      body: JSON.stringify({ rows }),
    }),
  /** @graph-connects /spec-requirements [calls] 総合テストの保存 */
  updateIntegrationTest: (
    pjKey: string,
    rows: SpecIntegrationTestRow[],
  ): Promise<SpecUpsertResult> =>
    request(`/spec-requirements/${encodeURIComponent(pjKey)}/integration-test`, {
      method: 'PUT',
      body: JSON.stringify({ rows }),
    }),
  /** @graph-connects /spec-requirements [calls] バリデーション一覧の保存 */
  updateValidationList: (pjKey: string, rows: SpecValidationRow[]): Promise<SpecUpsertResult> =>
    request(`/spec-requirements/${encodeURIComponent(pjKey)}/validation-list`, {
      method: 'PUT',
      body: JSON.stringify({ rows }),
    }),
  /** @graph-connects /spec-requirements [calls] 決定記録の保存 */
  updateDecisionLog: (pjKey: string, rows: SpecDecisionRow[]): Promise<SpecUpsertResult> =>
    request(`/spec-requirements/${encodeURIComponent(pjKey)}/decision-log`, {
      method: 'PUT',
      body: JSON.stringify({ rows }),
    }),
  /**
   * 補足欄を保存する。
   *
   * 文字数の上限は API が持つ（超えたら 400 とメッセージが返る）。画面側で数を持つと
   * 上限が変わったときに黙ってずれるので、**上限の判定は画面に写さない**。
   * @graph-connects /spec-requirements [calls] 補足欄の保存
   */
  updateProjectNotes: (pjKey: string, body: string): Promise<SpecUpsertResult> =>
    request(`/spec-requirements/${encodeURIComponent(pjKey)}/project-notes`, {
      method: 'PUT',
      body: JSON.stringify({ body }),
    }),
  /**
   * デザイン成果物を保存する。
   *
   * 渡した一覧で丸ごと置き換わる（案件に 1 つ）。件数の上限・URL のスキーム・名前の長さの判定は
   * **API が持つ**（超えたら 400 とメッセージが返る）。画面側で数やスキームを持つと、
   * API 側を変えたときに黙ってずれる。
   * @graph-connects /spec-requirements [calls] デザイン成果物の保存
   */
  updateDesignLinks: (pjKey: string, links: SpecDesignLink[]): Promise<SpecUpsertResult> =>
    request(`/spec-requirements/${encodeURIComponent(pjKey)}/design-links`, {
      method: 'PUT',
      body: JSON.stringify({ links }),
    }),
  /** @graph-connects /spec-requirements [calls] 次の 1 手 */
  nextStep: (pjKey: string): Promise<SpecNextStep> =>
    request(`/spec-requirements/${encodeURIComponent(pjKey)}/next-step`),
  /** @graph-connects /spec-requirements [calls] 成果物のフォーマット */
  format: (artifact: string): Promise<{ artifact: string; markdown: string }> =>
    request(`/spec-requirements/formats/${encodeURIComponent(artifact)}`),
  /** @graph-connects /spec-requirements [calls] 業務フローの保存 */
  updateFlow: (pjKey: string, flowId: string, data: SpecFlowInput): Promise<SpecUpsertResult> =>
    request(`/spec-requirements/${encodeURIComponent(pjKey)}/flows/${encodeURIComponent(flowId)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  /**
   * 業務フローを取り下げる（削除はしない）。理由の判定は API が持つので、画面は返ったメッセージを見せる。
   * @graph-connects /spec-requirements [calls] 業務フローの取り下げ
   */
  retireFlow: (pjKey: string, flowId: string, reason: string): Promise<SpecFlowRetireResult> =>
    request(
      `/spec-requirements/${encodeURIComponent(pjKey)}/flows/${encodeURIComponent(flowId)}/retire`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),
  /** @graph-connects /spec-requirements [calls] 業務フローの取り下げの解除 */
  restoreFlow: (pjKey: string, flowId: string): Promise<SpecFlowRetireResult> =>
    request(
      `/spec-requirements/${encodeURIComponent(pjKey)}/flows/${encodeURIComponent(flowId)}/restore`,
      { method: 'POST' },
    ),
  /**
   * 業務フローを保存せずに機械チェックする。
   *
   * 保存時と同じ判定を通す。案件に紐づかないので案件キーは渡さない。
   * @graph-connects /spec-requirements [calls] 業務フローの機械チェック
   */
  checkFlow: (data: SpecFlowInput): Promise<SpecFlowCheckResult> =>
    request('/spec-requirements/check/business-flow', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  /** @graph-connects /spec-requirements [calls] レビューの依頼 */
  requestReview: (pjKey: string, targetId: string): Promise<unknown> =>
    request(reviewPath(pjKey, targetId, 'request'), { method: 'POST' }),
  /** @graph-connects /spec-requirements [calls] レビューの提出 */
  submitReview: (pjKey: string, targetId: string, data: SpecReviewSubmitInput): Promise<unknown> =>
    request(reviewPath(pjKey, targetId, 'submit'), { method: 'POST', body: JSON.stringify(data) }),
  /** @graph-connects /spec-requirements [calls] DLE 承認の取り消し */
  revokeApproval: (pjKey: string, targetId: string): Promise<unknown> =>
    request(reviewPath(pjKey, targetId, 'revoke-approval'), { method: 'POST' }),
  /** @graph-connects /spec-requirements [calls] 案件全体の PL レビューの取得 */
  getPlReview: (pjKey: string): Promise<SpecPlReview> =>
    request(`/spec-requirements/${encodeURIComponent(pjKey)}/pl-review`),
  /** @graph-connects /spec-requirements [calls] 案件全体の PL の承認・差し戻し */
  submitPlReview: (pjKey: string, data: SpecPlReviewSubmitInput): Promise<SpecPlReview> =>
    request(`/spec-requirements/${encodeURIComponent(pjKey)}/pl-review`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  /** @graph-connects /spec-requirements [calls] 成果物ごとの PL の状態 */
  setPlArtifactStatus: (
    pjKey: string,
    artifact: string,
    data: SpecPlArtifactSubmitInput,
  ): Promise<SpecPlReview> =>
    request(
      `/spec-requirements/${encodeURIComponent(pjKey)}/pl-review/artifacts/${encodeURIComponent(artifact)}`,
      { method: 'PUT', body: JSON.stringify(data) },
    ),
  /** @graph-connects /spec-requirements [calls] 指摘の解決 */
  resolveFindings: (
    pjKey: string,
    targetId: string,
    resolutions: SpecFindingResolutionInput[],
  ): Promise<unknown> =>
    request(reviewPath(pjKey, targetId, 'resolve'), {
      method: 'POST',
      body: JSON.stringify({ resolutions }),
    }),
};
